/**
 * TOSS Web Application - Messenger Types
 * TypeScript types matching .NET 9 backend Messenger entities
 */

import type { FileProcessingStatus } from './api.types';

// ============================================================================
// Enums
// ============================================================================

export enum ChatType {
  Private = 'Private',
  Group = 'Group',
  Application = 'Application',
}

export enum MessageType {
  Text = 'Text',
  File = 'File',
  System = 'System',
  /**
   * Server-minted entity card (application card — decision Р27/Р29).
   * Never sent by a client: SendMessageRequest.messageType is whitelisted to
   * {Text, File} on the backend. Carries a human-readable `content` by contract
   * (§5), so any consumer that does not understand the type — Flutter, an old
   * web build — degrades to plain text instead of showing an empty bubble.
   */
  Card = 'Card',
}

export enum ChatMemberRole {
  Member = 'Member',
  Admin = 'Admin',
  Owner = 'Owner',
}

// ============================================================================
// Core Types
// ============================================================================

export interface ChatMember {
  id: string;
  userId: string;
  fullName: string;
  photoUrl?: string;
  role: ChatMemberRole;
  isOnline?: boolean;
  lastReadMessageId?: string;
  /**
   * Чью сторону представляет участник. По нему экран состава внешнего чата делится на две секции.
   *
   * Приходит с сервера (выводится из аудитории роли), а не считается на фронте: роли пользователя
   * в `ChatMember` нет, и вывести сторону здесь было бы не из чего.
   */
  side?: ChatMemberSide;
}

export interface ForwardedFromInfo {
  messageId?: string;
  chatId: string;
  senderId: string;
  senderName: string;
}

/**
 * Payload of a {@link MessageType.Card} message (contract §5).
 *
 * Sent as a JSON **object**, not a string: the backend serialises
 * `ChatMessage.PayloadJson` (jsonb) into the DTO field `payload`.
 * `kind` is the discriminator; `'application'` is the only kind rendered in Р1,
 * `'invoice'` (Р3) and `'document'` (Р6) are reserved. An unknown kind — or a
 * `null` payload, which the backend also returns when the stored JSON fails to
 * parse — must degrade to rendering `content` as plain text.
 *
 * `status` / `statusLabel` come from the **client** status dictionary
 * (ClientApplicationStatusMap), not from the internal ApplicationStatus enum:
 * one DB row is read by both audiences.
 */
export interface ChatCardPayload {
  kind: string;
  applicationId: string;
  applicationNumber: string;
  status: string;
  statusLabel: string;
  priority: string;
  direction: string | null;
  containersCount: number;
}

/**
 * Карточка рабочего события: что произошло с заявкой, пунктом обслуживания или её документами
 * (31.08.2026). Один вид карточки на девять событий — конкретное лежит в `event`.
 *
 * Заголовок, строки и подпись кнопки пишет сервер: одну и ту же строку в базе читают и заказчик,
 * и диспетчер, и текст, собранный на клиенте, рано или поздно разойдётся с текстом, собранным на
 * другом. Здесь остаётся только маршрут.
 */
export interface EventCardPayload {
  kind: string;
  event: string;
  title: string;
  lines: string[];
  actionLabel: string;
  applicationId: string;
  applicationNumber: string;
  applicationPtoId?: string | null;
  ptoName?: string | null;
  resultLabel?: string | null;
  reason?: string | null;
  action: string;
}

/** Всё, что может лежать в `payload` карточки. Неизвестный `kind` деградирует в `content`. */
export type MessengerCardPayload = ChatCardPayload | EventCardPayload;

/**
 * Одна эмодзи на сообщении, уже сгруппированная сервером: эмодзи, счётчик, свой ли он и кто
 * отреагировал (именами — идентификаторы здесь не нужны никому, кроме подсказки).
 */
export interface MessageReaction {
  emoji: string;
  count: number;
  reactedByMe: boolean;
  userNames: string[];
}

export interface ChatMessage {
  id: string;
  chatId: string;
  senderId: string;
  senderName: string;
  senderPhotoUrl?: string;
  content?: string;
  messageType: MessageType;
  /** Card payload — present only on MessageType.Card, `null` when unparseable. */
  payload?: MessengerCardPayload | null;
  replyTo?: {
    id: string;
    senderName: string;
    content?: string;
    isDeleted?: boolean;
    /**
     * The quoted message's file (25.09.2026, design D13): signed like `fileUrl`, all four null when
     * the quote is deleted, below the reader's history floor, or carried no file. Absent on
     * responses predating the fields.
     */
    fileUrl?: string | null;
    fileName?: string | null;
    fileContentType?: string | null;
    /** Signed `/api/files/{id}/poster` of a quoted video, once it has one. */
    posterUrl?: string | null;
  };
  forwardedFrom?: ForwardedFromInfo;
  fileUrl?: string;
  fileName?: string;
  fileContentType?: string;
  fileSize?: number;
  /**
   * Media facts (25.09.2026, design D13) — all nullable, all absent on responses predating them;
   * the feed degrades to what the browser can learn itself (first frame, metadata, 16:9).
   */
  /** Stored file id. `FileProcessed` matches cached messages on it. */
  fileId?: string | null;
  /** Signed `/api/files/{id}/poster?exp&sig` — only once a video has a poster. */
  posterUrl?: string | null;
  durationSeconds?: number | null;
  /** Server-side video compression; null when the message has no file. */
  processingStatus?: FileProcessingStatus | null;
  /** Video display size; null for images (design D15). */
  width?: number | null;
  height?: number | null;
  /**
   * Метка пачки вложений: сообщения одного отправителя, идущие подряд с одним и тем же значением,
   * лента показывает одним альбомом. Одно сообщение по-прежнему несёт один файл — группировка
   * нарисована поверх, а не заложена в контракт.
   */
  attachmentGroupId?: string;
  mentionedUserIds?: string[];
  isEdited: boolean;
  /** Эмодзи-реакции, сгруппированные сервером. Пустой массив, а не `null`. */
  reactions?: MessageReaction[];
  isDeleted: boolean;
  createdAt: string;
  /** Client-only: optimistic UI status for outgoing messages */
  _status?: 'sending' | 'failed';
  /** Client-only: fileId for retry of failed file messages */
  _fileId?: string;
}

export interface PinnedMessage {
  id: string;
  senderId: string;
  senderName: string;
  content?: string;
  messageType: MessageType;
  /**
   * Mirrors the backend addition of `payload` to PinnedMessageDto (contract §6).
   * The pinned banner deliberately renders `content` (always non-empty for a
   * card), so nothing reads this yet — it is here to keep the DTO mirror whole.
   */
  payload?: ChatCardPayload | null;
  fileName?: string;
  createdAt: string;
}

/**
 * Что это за чат компании-заказчика. Зеркало серверного `CustomerChatKind`.
 *
 * `'Internal'` — собственные чаты заказчика между своими — в этот тип не входит НАМЕРЕННО: они
 * вырезаны из всех ответов нашей стороне (`MessengerMappings.VisibleToTossSide`), включая ответы
 * администратору. Значение, которое невозможно получить, породило бы ветки, которые невозможно
 * выполнить и невозможно проверить.
 */
export type CustomerChatKind = 'Primary' | 'Shared';

/** Чью сторону представляет участник внешнего чата. */
export type ChatMemberSide = 'Toss' | 'Customer';

export interface ChatShort {
  id: string;
  name?: string;
  photoUrl?: string;
  chatType: ChatType;
  lastMessage?: string;
  lastMessageAt?: string;
  unreadCount: number;
  isPinned: boolean;
  isMuted: boolean;
  isArchived: boolean;
  applicationId?: string;
  pinnedMessage?: PinnedMessage | null;
  backgroundType?: 'color' | 'pattern' | 'image' | null;
  backgroundValue?: string | null;
  /** Компания-заказчик, которой принадлежит чат. Нет у внутренних чатов ТОСС. */
  customerId?: string | null;
  /**
   * Название этой компании — по нему папка «Клиенты» группирует строки.
   *
   * Отдельное поле, а не подстановка в `name`: у компании теперь несколько чатов, и заголовок
   * строки отвечает «про что», а заголовок группы — «чей».
   */
  customerName?: string | null;
  /**
   * `'Primary'` — общий чат компании с ТОСС: один на компанию, переименовать и удалить нельзя.
   * `'Shared'` — дополнительный чат, заведённый нашей стороной. Нет — внутренний чат ТОСС.
   */
  customerChatKind?: CustomerChatKind | null;
  /** Whether the caller may delete this chat just for themselves (`scope=me`). Server-computed. */
  canDeleteForMe: boolean;
  /** Whether the caller may delete this chat for every participant, permanently (`scope=everyone`). */
  canDeleteForEveryone: boolean;
  members: ChatMember[];
}

export interface ChatFull extends ChatShort {
  description?: string;
  createdBy: string;
  /**
   * True for the external conversation with a customer company.
   *
   * The roster rules differ there: any internal member may invite and remove (not just the chat's
   * owner), and inviting a mechanic asks whether to hand over the conversation that happened before
   * them. The «Клиенты» folder answers this for the chat list; the info modal has no folder context.
   */
  isCustomerChat?: boolean;
}

// ============================================================================
// Response Types
// ============================================================================

export interface ChatListResponse {
  items: ChatShort[];
  pageNumber: number;
  totalPages: number;
  hasNextPage: boolean;
  archiveCount: number;
}

export interface MessageListResponse {
  items: ChatMessage[];
  hasMore: boolean;
  nextCursor?: string;
}

export interface SearchResult {
  messageId: string;
  chatId: string;
  chatName?: string;
  senderName: string;
  content: string;
  highlight: string;
  createdAt: string;
}

export interface SearchResultResponse {
  items: SearchResult[];
  totalCount: number;
  hasMore: boolean;
  nextCursor?: string | null;
}

export interface UnreadCountResponse {
  count: number;
}

// ============================================================================
// Request Types
// ============================================================================

export interface CreateChatRequest {
  chatType: ChatType;
  name?: string;
  memberIds: string[];
}

/**
 * Заявка на дополнительный чат с компанией-заказчиком.
 *
 * Две стороны — два списка, а не один `memberIds`: так сервер отвечает точной ошибкой («этот
 * аккаунт не в выбранной компании» против «это внешний аккаунт в списке наших»), а
 * `includeAllCustomerUsers` относится ровно к одной половине.
 */
export interface CreateCustomerChatRequest {
  customerId: string;
  name: string;
  customerUserIds: string[];
  staffUserIds: string[];
  /** «Все сотрудники заказчика» — снимок на момент создания, не живое правило. */
  includeAllCustomerUsers?: boolean;
}

/** Сотрудник заказчика как кандидат в участники. Без почты и телефона — это пикер, не карточка. */
export interface CustomerChatCandidate {
  id: string;
  fullName: string;
  photoUrl?: string | null;
  /**
   * Должность — свободная строка. Единственное, что в системе отличает водителя от снабженца:
   * типов сотрудников у заказчика нет, роль в кабинете говорит о правах, а не о профессии.
   */
  position?: string | null;
  clientRole: 'Owner' | 'Administrator' | 'Employee' | 'Observer';
  isPortalOwner: boolean;
  /** Уже стоит в общем чате компании — подсказка, а не ограничение. */
  isInPrimaryChat: boolean;
}

export interface UpdateChatRequest {
  name?: string;
  photoUrl?: string;
  description?: string;
  backgroundType?: string;
  backgroundValue?: string;
  isMuted?: boolean;
}

export interface SendMessageRequest {
  content?: string;
  messageType?: MessageType;
  replyToMessageId?: string;
  fileId?: string;
  fileName?: string;
  fileContentType?: string;
  fileSize?: number;
  /** Один GUID на всю пачку вложений — см. `ChatMessage.attachmentGroupId`. */
  attachmentGroupId?: string;
  mentionedUserIds?: string[];
}

export interface EditMessageRequest {
  content: string;
}

export interface ForwardMessagesRequest {
  messageIds: string[];
  comment?: string;
}

// ============================================================================
// Filter Types
// ============================================================================

/**
 * Server-side chat folders. `'clients'` (Р1) holds the external company chats;
 * adding it here flows automatically into messengerStore and ChatList, which
 * both type the active folder as Exclude<ChatFolder, 'archive'>. The active
 * folder is not persisted (store default is 'chats'), so no migration is needed.
 */
export type ChatFolder = 'chats' | 'applications' | 'clients' | 'archive';

export interface ChatListFilter {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  folder?: ChatFolder;
  /** Сузить до чатов одной компании — для секции «Чаты компании» в карточке заказчика. */
  customerId?: string;
  /** Only chats the caller is a member of — the «Переслать в чат» picker, not the role-wide list. */
  memberOnly?: boolean;
}

export interface MessageListFilter {
  cursor?: string;
  pageSize?: number;
}

// ============================================================================
// SignalR Hub Methods
// ============================================================================

export const MessengerHubMethods = {
  // Server-to-client methods
  ReceiveMessage: 'ReceiveMessage',
  MessageEdited: 'MessageEdited',
  MessageDeleted: 'MessageDeleted',
  UserTyping: 'UserTyping',
  UserStoppedTyping: 'UserStoppedTyping',
  MessageRead: 'MessageRead',
  ChatCreated: 'ChatCreated',
  ChatUpdated: 'ChatUpdated',
  UnreadCountChanged: 'UnreadCountChanged',
  ChatUnreadIncremented: 'ChatUnreadIncremented',

  MemberAdded: 'MemberAdded',
  MemberRemoved: 'MemberRemoved',
  MemberRoleChanged: 'MemberRoleChanged',
  MessagePinned: 'MessagePinned',
  ChatDeleted: 'ChatDeleted',
  MessageReactionsChanged: 'MessageReactionsChanged',
  /** Video release (2026-09-23) — server-side compression finished for a file of this chat. */
  FileProcessed: 'FileProcessed',

  // Client-to-server methods
  SendTyping: 'SendTyping',
  StopTyping: 'StopTyping',
} as const;

// ============================================================================
// SignalR Event Types
// ============================================================================

// Backend sends ChatMessageDto directly (not wrapped)
export type ReceiveMessageEvent = ChatMessage;

// Backend sends ChatMessageDto directly for edits too
export type MessageEditedEvent = ChatMessage;

/**
 * Реакции сообщения после того, как кто-то поставил или снял свою.
 *
 * Приходит ПОЛНЫЙ набор, а не дельта: клиент, пропустивший одно событие, иначе накапливает
 * расхождение в счётчиках. `reactedByMe` в рассылке всегда `false` — читателя на момент отправки
 * не существует, и каждый клиент отмечает своё сам (по `userNames` или по ответу своей мутации).
 */
export interface MessageReactionsChangedEvent {
  chatId: string;
  messageId: string;
  reactions: MessageReaction[];
}

export interface MessageDeletedEvent {
  chatId: string;
  messageId: string;
  lastMessagePreview?: string | null;
  lastMessageAt?: string | null;
}

/**
 * Video release (2026-09-23). Sent once per `(chatId, fileId)` pair to `chat_{chatId}` — every
 * chat holding a message with that file gets its own event. Fired even when the compressed
 * output was not smaller than the source and the source was kept (only poster/metadata are new —
 * a client cannot tell the two cases apart from `contentType` alone). The message's own
 * `fileName`/`fileContentType`/`fileSize` are rewritten server-side at the same moment; a client
 * that missed the event still gets correct values on its next fetch. `messages.md` §fileId,
 * `signalr-hub.md` §FileProcessedEvent.
 */
export interface FileProcessedEvent {
  chatId: string;
  fileId: string;
  /** New `.mp4` name once compressed; unchanged if the source was kept. */
  fileName: string;
  /** `"video/mp4"` once compressed. */
  contentType: string;
  size: number;
  /** Signed, minted fresh — never cache beyond this event. */
  posterUrl: string | null;
  durationSeconds: number | null;
  /** Video display size (25.09.2026) — absent from a server that predates it. */
  width?: number | null;
  height?: number | null;
  /** Always `'Ready'` when sent; absent from a server that predates the field. */
  processingStatus?: FileProcessingStatus | null;
}

export interface UserTypingEvent {
  chatId: string;
  userId: string;
  userName: string;
}

export interface StopTypingEvent {
  chatId: string;
  userId: string;
}

export interface MessageReadEvent {
  chatId: string;
  userId: string;
  messageId: string;
}

export interface MemberAddedEvent {
  chatId: string;
  member: ChatMember;
}

export interface MemberRemovedEvent {
  chatId: string;
  userId: string;
}

export interface MemberRoleChangedEvent {
  chatId: string;
  userId: string;
  newRole: ChatMemberRole;
}

export interface ChatDeletedEvent {
  chatId: string;
}

export interface UnreadCountChangedEvent {
  totalUnread: number;
}

/**
 * Per-chat unread count increment event. Additive to UnreadCountChanged —
 * backend sends both so legacy clients keep working while new clients can
 * update a single chat's sidebar badge without refetching the full list.
 */
export interface ChatUnreadIncrementedEvent {
  chatId: string;
  byUserId: string;
  newChatUnreadCount: number;
}

export interface MessagePinnedEvent {
  chatId: string;
  pinnedMessage: PinnedMessage | null;
  pinnedByUserId: string;
  pinnedByUserName: string;
}

export interface MessageContextResponse {
  items: ChatMessage[];
  hasBefore: boolean;
  hasAfter: boolean;
  beforeCursor?: string;
  afterCursor?: string;
}
